import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, CheckCircle, XCircle, ChevronRight, Loader2 } from '@/lib/icons';
import ImprovementSuggestions from '@/components/ImprovementSuggestions';
import FacilitatorLayout from '@/components/FacilitatorLayout';
import { Header } from '@/components/prosci/Header';

// Helper for color
const getScoreColor = (avg) => {
  if (avg == null || avg === 0) return 'bg-neutral-200 text-neutral-600';
  if (avg < 1.5) return 'bg-score-1 text-score-1-foreground';
  if (avg < 2.5) return 'bg-score-2 text-score-2-foreground';
  if (avg < 3.5) return 'bg-score-3 text-score-3-foreground';
  if (avg < 4.5) return 'bg-score-4 text-score-4-foreground';
  return 'bg-score-5 text-score-5-foreground';
};

// Helper for quick_interactive factor badges (S/O/M)
const getQuickInteractiveFactorBadge = (avg) => {
  if (avg == null) return { text: '—', style: 'bg-neutral-200 text-neutral-600' };
  if (avg === 4.0) return { text: 'S', style: 'bg-score-5 text-score-5-foreground' };
  if (avg === 2.0) return { text: 'O', style: 'bg-score-3 text-score-3-foreground' };
  if (avg > 2.0 && avg < 4.0) {
    // Mixed response - determine leaning
    if (avg < 3.0) {
      return { text: 'Mixed / O', style: 'bg-neutral-300 text-neutral-700' };
    } else if (avg > 3.0) {
      return { text: 'Mixed / S', style: 'bg-neutral-300 text-neutral-700' };
    } else {
      return { text: 'Mixed', style: 'bg-neutral-300 text-neutral-700' };
    }
  }
  // Fallback for other specific scores (e.g., 1, 3, 5) if they occur
  return { text: Math.round(avg), style: getScoreColor(avg) };
};

// Helper for quick_interactive individual participant badges (S/O)
const getQuickInteractiveParticipantBadge = (score) => {
  const numScore = Number(score);
  if (score == null || isNaN(numScore)) return { text: '—', style: 'bg-neutral-200 text-neutral-600' };
  if (numScore === 4) return { text: 'S', style: 'bg-score-5 text-score-5-foreground' };
  if (numScore === 2) return { text: 'O', style: 'bg-score-3 text-score-3-foreground' };
  return { text: String(score), style: getScoreColor(numScore) }; // Show score as string
};

const FacilitatorAssessmentSessionShow = ({ assessmentSession, reportUrl, backUrl, currentUser }) => {
  const [selectedContributorId, setSelectedContributorId] = useState('');
  const [selectedFactorId, setSelectedFactorId] = useState('');
  const [showAllComments, setShowAllComments] = useState(false);

  // Memoize stats for performance
  const stats = useMemo(() => {
    const responses = assessmentSession.assessmentResponses || [];
    // Best Practice stats: avg, responseCount, breakdown, and per-participant scores
    const factorStats = {};
    (assessmentSession.structure || []).forEach(contributor => {
      (contributor.factors || []).forEach(factor => {
        const factorResponses = responses.filter(r => r.factorId === factor.id && r.response && !isNaN(Number(r.response)));
        const scores = factorResponses.map(r => Number(r.response)).filter(n => n > 0);
        const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
        // Score breakdown
        const breakdown = [0, 0, 0, 0, 0, 0]; // 0 unused, 1-5
        scores.forEach(score => { breakdown[score] = (breakdown[score] || 0) + 1; });
        // Per participant
        const byParticipant = factorResponses.map(r => ({ participant_identifier: r.participantIdentifier, score: r.response }));
        factorStats[factor.id] = {
          id: factor.id,
          text: factor.text,
          avg,
          responseCount: scores.length,
          breakdown,
          byParticipant,
        };
      });
    });
    // Contributor stats: count factors averaging as opportunity/strength
    const contributorStats = (assessmentSession.structure || []).map(contributor => {
      let opp = 0, str = 0;
      (contributor.factors || []).forEach(factor => {
        const stat = factorStats[factor.id];
        if (stat && stat.avg != null) {
          if (stat.avg < 2.5) opp++;
          if (stat.avg >= 3.5) str++;
        }
      });
      return { id: contributor.id, name: contributor.name, opp, str };
    });
    return { contributorStats, factorStats };
  }, [assessmentSession]);

  const contributors = assessmentSession.structure || [];
  const selectedContributor = contributors.find(c => c.id === selectedContributorId);
  const factors = selectedContributor ? selectedContributor.factors || [] : [];
  const selectedFactor = factors.find(f => f.id === selectedFactorId);
  const selectedFactorStats = selectedFactor ? stats.factorStats[selectedFactor.id] : null;
  const assessmentType = assessmentSession.assessmentType;

  // Limit comments displayed initially
  const displayedParticipants = selectedFactorStats?.byParticipant || [];
  const visibleParticipants = showAllComments ? displayedParticipants : displayedParticipants.slice(0, 3);
  const hasMoreComments = displayedParticipants.length > 3;

  return (
    <FacilitatorLayout userName={currentUser?.name || 'Facilitator'}>
      <Header
        title="Assessment Session Details"
        size="small"
        breadcrumb={
          <nav className="flex items-center gap-1.5 text-sm">
            <a href={backUrl}>Sessions</a>
            <ChevronRight className="h-3 w-3" />
            <span>{assessmentSession.name || 'Session Details'}</span>
          </nav>
        }
      >
        <Button asChild variant="lilac" className="shrink-0 self-center">
          <a href={reportUrl}>View Report</a>
        </Button>
      </Header>
      <div className="container mx-auto p-4 md:p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle></CardTitle>
          </CardHeader>
          <CardContent>
            {/* Selection Controls */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-xl mb-4">Select Contributor</h3>
                <Select
                  value={selectedContributorId}
                  onValueChange={(val) => {
                    setSelectedContributorId(val)
                    setSelectedFactorId('')
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a contributor..." />
                  </SelectTrigger>
                  <SelectContent className="w-full max-w-lg max-h-[60vh] overflow-y-auto">
                    {stats.contributorStats.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="flex flex-row items-center w-full">
                        <span className="flex-1 ">{c.name}</span>
                        <span className="ml-2">
                          <span className="px-2 py-0.5 rounded-l-full rounded-r-none text-xs font-semibold bg-score-5 text-score-5-foreground whitespace-nowrap">{c.str}</span>
                          <span className="px-2 py-0.5 rounded-r-full rounded-l-none text-xs font-semibold bg-score-3 text-score-3-foreground whitespace-nowrap">{c.opp}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <h3 className="text-xl mb-4">Select Best Practice</h3>
                <Select value={selectedFactorId} onValueChange={setSelectedFactorId} disabled={!selectedContributor}>
                  <SelectTrigger>
                    <SelectValue placeholder={selectedContributor ? 'Choose a best practice...' : 'Select a contributor first'} />
                  </SelectTrigger>
                  <SelectContent className="w-full max-w-xl max-h-[60vh] overflow-y-auto">
                    {factors.map((factor) => {
                      const stat = stats.factorStats[factor.id]
                      const avgScore = stat ? stat.avg : null

                      let badgeText
                      let badgeStyle

                      if (assessmentType === 'quick_interactive') {
                        const badgeInfo = getQuickInteractiveFactorBadge(avgScore)
                        badgeText = badgeInfo.text
                        badgeStyle = badgeInfo.style
                      } else {
                        badgeText = avgScore != null ? Math.round(avgScore) : '—'
                        badgeStyle = getScoreColor(avgScore)
                      }

                      return (
                        <SelectItem key={factor.id} value={factor.id} className="w-full max-w-xl pr-4">
                          <span className="" title={factor.text}>
                            {factor.text.length > 60 ? `${factor.text.substring(0, 60)}...` : factor.text}
                          </span>
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 whitespace-nowrap ${badgeStyle}`}>{badgeText}</span>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedContributor && selectedFactor && (
              <>
              <div className="border-t border-border my-6"></div>
              <div className="grid md:grid-cols-12 gap-6">
                {/* Left Column - Comments */}
                <div className="md:col-span-4 space-y-4">
                  <h3 className="text-xl mb-3">Participant Responses</h3>

                  {selectedFactorStats && (
                    <div className="bg-background rounded-lg border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold">Total Respondents:</span>
                        <div className="flex items-center gap-2">
                          <span>{selectedFactorStats.responseCount}</span>
                          {(() => {
                            const selectedFactorAvg = selectedFactorStats ? selectedFactorStats.avg : null
                            let totalResponsesBadgeText
                            let totalResponsesBadgeStyle

                            if (assessmentType === 'quick_interactive') {
                              const badgeInfo = getQuickInteractiveFactorBadge(selectedFactorAvg)
                              totalResponsesBadgeText = badgeInfo.text // No "Avg: " prefix
                              totalResponsesBadgeStyle = badgeInfo.style
                            } else {
                              totalResponsesBadgeText = selectedFactorAvg != null ? `Avg: ${Math.round(selectedFactorAvg)}` : 'Avg: —'
                              totalResponsesBadgeStyle = getScoreColor(selectedFactorAvg)
                            }
                            return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${totalResponsesBadgeStyle}`}>{totalResponsesBadgeText}</span>
                          })()}
                        </div>
                      </div>

                      {/* Participant scores */}
                      {visibleParticipants.length > 0 ? (
                        <div>
                          <ul className="space-y-2 mb-2">
                            {visibleParticipants.map((entry, idx) => {
                              let participantBadgeText
                              let participantBadgeStyle

                              if (assessmentType === 'quick_interactive') {
                                const badgeInfo = getQuickInteractiveParticipantBadge(entry.score)
                                participantBadgeText = badgeInfo.text
                                participantBadgeStyle = badgeInfo.style
                              } else {
                                participantBadgeText = entry.score
                                participantBadgeStyle = getScoreColor(Number(entry.score))
                              }
                              return (
                                <li key={idx} className="flex items-center justify-between bg-card p-2 rounded border">
                                  <span className="text-sm font-medium truncate mr-2" title={entry.participant_identifier}>
                                    {entry.participant_identifier}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${participantBadgeStyle}`}>{participantBadgeText}</span>
                                </li>
                              )
                            })}
                          </ul>

                          {hasMoreComments && (
                            <Button variant="ghost" size="sm" className="text-muted-foreground mt-2" onClick={() => setShowAllComments(!showAllComments)}>
                              {showAllComments ? (
                                <>
                                  <ChevronUp className="h-4 w-4 mr-1" />
                                  Show Less
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="h-4 w-4 mr-1" />
                                  Show All ({displayedParticipants.length})
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No responses recorded yet</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Right Column - Actions to Ensure Change Success Form */}
                <div className="md:col-span-8">
                  <h3 className="text-xl mb-3">Actions to Ensure Change Success</h3>
                  <ImprovementSuggestions accessCode={assessmentSession.accessCode} contributorId={selectedContributor.id} factorId={selectedFactor.id} />
                </div>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </FacilitatorLayout>
  )
};

FacilitatorAssessmentSessionShow.propTypes = {
  assessmentSession: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    projectName: PropTypes.string,
    assessmentType: PropTypes.string.isRequired,
    accessCode: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    expiresAt: PropTypes.string,
    assessmentResponses: PropTypes.arrayOf(PropTypes.shape({
      factorId: PropTypes.string.isRequired,
      response: PropTypes.number.isRequired
    })),
    structure: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      factors: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired
      }))
    }))
  }).isRequired,
  reportUrl: PropTypes.string.isRequired,
  backUrl: PropTypes.string.isRequired,
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string
  })
};

export default FacilitatorAssessmentSessionShow; 